import { Button, Flex, Group, Stack, Text } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { IconPlus } from '@tabler/icons-react';
import { useQuery } from '@tanstack/react-query';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { Content } from '@/core/components';

import { useCurrentUser } from '@feature/security';

import { getTravelDocumentKindsOptions } from '../../model/TravelRequestQuery';
import {
  TravelFileKind,
  TravelRequest,
  TravelRequestFile,
  canManageTravelHrMemo,
  hasTravelMobilitySupport,
  isTravelReturnDatePending,
  isTravelReturnFileKind,
  travelRequestPermissions,
  travelReturnUploadKinds,
  travelSubmissionUploadKinds,
} from '../../model/TravelRequestTypes';
import { TravelRequestReturnDateNotice } from '../TravelRequestReturnDateNotice';
import { TravelRequestDocumentChecklist } from './TravelRequestDocumentChecklist';
import { TravelRequestFileRow } from './TravelRequestFileRow';
import { TravelRequestFileUploadModal } from './TravelRequestFileUploadModal';
import { TravelRequestReturnChecklist } from './TravelRequestReturnChecklist';

export type TravelFileStage = 'submission' | 'return';

const hrMemoKinds = ['HR_LEAVE_MEMO'] as const satisfies readonly TravelFileKind[];

interface TravelRequestFilesProps {
  request: TravelRequest;
  stage?: TravelFileStage;
}

export function TravelRequestFiles({ request, stage }: TravelRequestFilesProps) {
  const { t } = useTranslation();
  const [uploadOpened, { open: openUpload, close: closeUpload }] = useDisclosure(false);
  const [hrMemoOpened, { open: openHrMemo, close: closeHrMemo }] = useDisclosure(false);

  const currentUser = useCurrentUser();
  const isRequester = request.requester.id === currentUser.id;
  const { canEdit } = travelRequestPermissions(request.status, isRequester, currentUser.currentRole);
  const returnDatePending = isTravelReturnDatePending(request, isRequester, currentUser.currentRole);
  const canManageReturnFiles = request.canComplete || returnDatePending;
  const showSubmission = stage !== 'return';
  const showReturn = stage !== 'submission';
  const returnStage = request.status === 'APPROVED' || request.status === 'COMPLETED';
  // A cancelled request keeps listing what it gathered after approval, but its return documents are no longer owed.
  const afterApproval = returnStage || request.status === 'CANCELLED';
  // An "other" file lists with the stage it was most likely uploaded in, which flips once the request is approved.
  const inReturnStage = (kind: TravelFileKind) => isTravelReturnFileKind(kind) || (kind === 'OTHER' && afterApproval);
  const files = request.files.filter(
    file => file.kind !== 'ADMINISTRATIVE_BOARD_DECISION' && (inReturnStage(file.kind) ? showReturn : showSubmission),
  );
  // Until the lists load the dialog offers every kind of its stage, and the server refuses what the type does not take.
  const { data: documentKinds } = useQuery(
    getTravelDocumentKindsOptions(request.activityType, hasTravelMobilitySupport(request)),
  );
  const uploadKinds =
    showSubmission && canEdit
      ? travelSubmissionUploadKinds(request, documentKinds)
      : travelReturnUploadKinds(documentKinds, returnDatePending);
  const canUpload = ((showSubmission && canEdit) || (showReturn && canManageReturnFiles)) && uploadKinds.length > 0;
  const canManageHrMemo = canManageTravelHrMemo(request, isRequester, currentUser.currentRole);
  const canAttachHrMemo = showSubmission && canManageHrMemo;
  const canDeleteFile = (requestFile: TravelRequestFile) =>
    requestFile.kind === 'HR_LEAVE_MEMO'
      ? canManageHrMemo
      : canEdit || (canManageReturnFiles && inReturnStage(requestFile.kind));
  const showReturnChecklist = showReturn && returnStage;

  // Otherwise the dialog would come back by itself the next time uploading is allowed.
  useEffect(() => {
    if (!canUpload) {
      closeUpload();
    }
  }, [canUpload, closeUpload]);

  return (
    <Content type="full">
      <Flex
        justify="space-between"
        align="center"
      >
        <Text fw="500">{t('travel.requests.view.files')}</Text>
        <Group gap="xs">
          {canAttachHrMemo && (
            <Button
              size="xs"
              leftSection={<IconPlus size={14} />}
              onClick={openHrMemo}
            >
              {t('travel.requests.view.attachHrMemo')}
            </Button>
          )}
          {canUpload && (
            <Button
              size="xs"
              leftSection={<IconPlus size={14} />}
              onClick={openUpload}
            >
              {t('travel.requests.view.uploadFile')}
            </Button>
          )}
        </Group>
      </Flex>

      {showReturn && <TravelRequestReturnDateNotice request={request} />}
      {showSubmission && <TravelRequestDocumentChecklist request={request} />}
      {showReturnChecklist && <TravelRequestReturnChecklist request={request} />}

      {files.length === 0 ? (
        <Text
          size="sm"
          c="dimmed"
          fs="italic"
        >
          {t('travel.requests.view.noFiles')}
        </Text>
      ) : (
        <Stack gap="xs">
          {files.map(requestFile => (
            <TravelRequestFileRow
              key={requestFile.id}
              requestId={request.id}
              requestFile={requestFile}
              activityType={request.activityType}
              canDelete={canDeleteFile(requestFile)}
            />
          ))}
        </Stack>
      )}

      {uploadOpened && canUpload && (
        <TravelRequestFileUploadModal
          requestId={request.id}
          activityType={request.activityType}
          kinds={uploadKinds}
          onClose={closeUpload}
        />
      )}
      {hrMemoOpened && (
        <TravelRequestFileUploadModal
          requestId={request.id}
          activityType={request.activityType}
          kinds={hrMemoKinds}
          onClose={closeHrMemo}
        />
      )}
    </Content>
  );
}
